import { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Package,
  CircleSlash,
  Settings2,
  Sparkles,
  TrendingUp,
  Users,
  ShoppingBag,
  AlertCircle,
  Plus,
  Pencil,
} from 'lucide-react';
import { HeartPulse, ChevronRight } from 'lucide-react';
import { usePets } from '../../contexts/PetsContext';
import { useFeedingGroups } from '../../contexts/FeedingGroupsContext';
import { useHydrationGroups } from '../../contexts/HydrationGroupsContext';
import { useHealth } from '../../contexts/HealthContext';
import { useFamily } from '../../contexts/FamilyContext';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import Avatar from '../../components/ui/Avatar';
import StatusTrafficLight from './components/StatusTrafficLight';
import FeedingButton from './components/FeedingButton';
import HydrationCard from './components/HydrationCard';
import RefillModal from './components/RefillModal';
import ConfirmEmptyModal from './components/ConfirmEmptyModal';
import SyncStockModal from './components/SyncStockModal';
import OpenBagModal from './components/OpenBagModal';
import AddExtraBagsModal from './components/AddExtraBagsModal';
import LogTimeline from './components/LogTimeline';
import { useFeedingStatus } from '../../hooks/useFeedingStatus';
import { usePetPhoto } from '../../hooks/usePetPhoto';
import { getPetEstimations } from '../../services/petService';
import { formatGrams, formatTimeAgo, formatHours } from '../../lib/format';
import { LEARNING_STATUS, LEARNING, ROUTES } from '../../config/constants';
import { TYPE_META as HEALTH_TYPE_META } from '../health/components/HealthRecordTypeChips';

const LearningBanner = ({ pet, group }) => {
  const remaining = Math.max(
    0,
    LEARNING.MIN_CYCLES_TO_CALIBRATE - (group.cyclesCompleted ?? 0)
  );
  return (
    <div className="flex items-start gap-3 rounded-xl bg-brand-50 px-4 py-3 ring-1 ring-brand-100">
      <Sparkles className="h-5 w-5 shrink-0 text-brand-700 mt-0.5" aria-hidden="true" />
      <div className="flex-1">
        <p className="text-sm font-medium text-brand-900">Aprendiendo los hábitos de {pet.name}</p>
        <p className="text-xs text-brand-800 mt-0.5">
          {remaining === LEARNING.MIN_CYCLES_TO_CALIBRATE
            ? 'Después del primer rellenado completo vamos a empezar a estimar cuánto le dura el dispenser.'
            : `Faltan ${remaining} ciclo${remaining === 1 ? '' : 's'} para tener estimaciones precisas.`}
        </p>
      </div>
    </div>
  );
};

const EstimationsCard = ({ pet, group }) => {
  const est = getPetEstimations(pet, group);
  if (!est) return null;

  const isCalibrated = est.learningStatus === LEARNING_STATUS.CALIBRATED;
  const confidenceCopy = {
    low: 'Estimación inicial',
    medium: 'Confianza media',
    high: 'Confianza alta',
  }[est.confidence];

  return (
    <Card>
      <div className="flex items-start gap-3 mb-3">
        <div className="rounded-lg bg-emerald-50 p-2">
          <TrendingUp className="h-5 w-5 text-emerald-700" aria-hidden="true" />
        </div>
        <div className="flex-1">
          <h2 className="text-sm font-semibold text-slate-900">Estimaciones</h2>
          <p className="text-xs text-slate-500 mt-0.5">{confidenceCopy}</p>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-lg bg-slate-50 px-3 py-2.5">
          <p className="text-xs text-slate-500">Comidas restantes</p>
          <p className="text-2xl font-bold text-slate-900 mt-0.5 leading-none">
            ~{est.learned}
          </p>
          {isCalibrated && est.strict !== est.learned && (
            <p className="text-[10px] text-slate-400 mt-1">
              ({est.strict} si dura el promedio exacto)
            </p>
          )}
        </div>
        <div className="rounded-lg bg-slate-50 px-3 py-2.5">
          <p className="text-xs text-slate-500">Días aprox.</p>
          <p className="text-2xl font-bold text-slate-900 mt-0.5 leading-none">
            {est.daysRemaining != null ? `~${est.daysRemaining}` : '—'}
          </p>
          <p className="text-[10px] text-slate-400 mt-1">
            del dispenser actual
          </p>
        </div>
      </div>
    </Card>
  );
};

const HealthMiniSection = ({ petId }) => {
  const { getRecordsForPet } = useHealth();
  const records = getRecordsForPet(petId);
  const top3 = records.slice(0, 3);
  const fullPath = ROUTES.PET_HEALTH.replace(':petId', petId);

  if (records.length === 0) {
    return (
      <Card>
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-2">
            <div className="rounded-lg bg-rose-50 p-2">
              <HeartPulse className="h-5 w-5 text-rose-700" aria-hidden="true" />
            </div>
            <div>
              <h2 className="text-sm font-semibold text-slate-900">Salud</h2>
              <p className="text-xs text-slate-500 mt-0.5">Sin observaciones todavía</p>
            </div>
          </div>
          <Button as={Link} to={fullPath} variant="secondary" size="sm">
            Agregar
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-start gap-2">
          <div className="rounded-lg bg-rose-50 p-2">
            <HeartPulse className="h-5 w-5 text-rose-700" aria-hidden="true" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-slate-900">Salud</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              {records.length} observación{records.length === 1 ? '' : 'es'}
            </p>
          </div>
        </div>
        <Link
          to={fullPath}
          className="inline-flex items-center gap-0.5 text-xs font-medium text-brand-700 hover:text-brand-800"
        >
          Ver todas <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />
        </Link>
      </div>
      <ul className="flex flex-col gap-2">
        {top3.map((r) => {
          const meta = HEALTH_TYPE_META[r.type] ?? HEALTH_TYPE_META.note;
          const Icon = meta.icon;
          const ts = r.occurredAt?.toMillis?.() ?? null;
          return (
            <li key={r.id} className="flex items-start gap-2.5">
              <div className={['flex h-7 w-7 shrink-0 items-center justify-center rounded-lg', meta.className].join(' ')}>
                <Icon className="h-3.5 w-3.5" aria-hidden="true" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-slate-900 leading-tight truncate">{r.title}</p>
                {ts && (
                  <p className="text-xs text-slate-400 mt-0.5">{formatTimeAgo(ts)}</p>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </Card>
  );
};

const PetDetailPage = () => {
  const { petId } = useParams();
  const navigate = useNavigate();
  const { pets, getPetById, isLoading, fetchPetLogs } = usePets();
  const { groups, getGroupById, isLoading: groupsLoading } = useFeedingGroups();
  const { members } = useFamily();

  const { groups: hydrationGroupsList, getGroupById: getHydrationGroupById } =
    useHydrationGroups();

  const pet = getPetById(petId);
  const group = pet ? getGroupById(pet.feedingGroupId) : null;
  const hydrationGroup = pet?.hydrationGroupId
    ? getHydrationGroupById(pet.hydrationGroupId)
    : null;
  const feedingStatus = useFeedingStatus(pet);
  const { url: petPhotoUrl } = usePetPhoto(pet);

  // Otras mascotas del mismo grupo (para mostrar "compartido con")
  const groupMates = useMemo(() => {
    if (!group) return [];
    return pets.filter(
      (p) => p.feedingGroupId === group.id && p.id !== petId
    );
  }, [pets, group, petId]);

  // Otras mascotas del mismo balde de agua (para mostrar "compartido con" en HydrationCard)
  const hydrationMates = useMemo(() => {
    if (!hydrationGroup) return [];
    return pets.filter(
      (p) => p.hydrationGroupId === hydrationGroup.id && p.id !== petId && !p.deletedAt
    );
  }, [pets, hydrationGroup, petId]);

  const lastWateredByName = useMemo(() => {
    if (!hydrationGroup?.lastWateredBy) return null;
    const member = members?.find?.((m) => m.uid === hydrationGroup.lastWateredBy);
    return member?.displayName || null;
  }, [hydrationGroup?.lastWateredBy, members]);

  const [logs, setLogs] = useState([]);
  const [logsStatus, setLogsStatus] = useState('idle');

  // Cuando hay varios dispensers, los modales operan sobre uno específico.
  // El state guarda el dispenser elegido al abrir, no solo el flag.
  const [refillOpen, setRefillOpen] = useState(null); // dispenser | null
  const [confirmEmptyOpen, setConfirmEmptyOpen] = useState(null); // dispenser | null
  const [syncStockOpen, setSyncStockOpen] = useState(false);
  const [openBagOpen, setOpenBagOpen] = useState(false);
  const [addBagsOpen, setAddBagsOpen] = useState(false);

  // Refetch de logs cuando cambia el grupo (lo refleja updatedAt del grupo).
  const groupUpdatedAtMs = group?.updatedAt?.toMillis?.() ?? null;

  const loadLogs = useCallback(async () => {
    if (!petId || !group) return;
    try {
      setLogsStatus('loading');
      const fresh = await fetchPetLogs(petId, { limit: 20 });
      setLogs(fresh);
      setLogsStatus('ready');
    } catch (err) {
      console.error('[PetDetailPage] Failed to load logs:', err);
      setLogsStatus('error');
    }
  }, [petId, fetchPetLogs, group]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs, groupUpdatedAtMs]);

  if (isLoading || groupsLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Button variant="ghost" size="sm" leftIcon={ArrowLeft} onClick={() => navigate(-1)} className="self-start">
          Volver
        </Button>
        <Card>
          <p className="text-sm text-slate-500">Cargando...</p>
        </Card>
      </div>
    );
  }

  if (!pet) {
    return (
      <div className="flex flex-col gap-4">
        <Button variant="ghost" size="sm" leftIcon={ArrowLeft} onClick={() => navigate(-1)} className="self-start">
          Volver
        </Button>
        <Card>
          <p className="text-sm text-slate-600">
            No encontramos esta mascota. Puede haber sido eliminada.
          </p>
        </Card>
      </div>
    );
  }

  if (!group) {
    return (
      <div className="flex flex-col gap-4">
        <Button variant="ghost" size="sm" leftIcon={ArrowLeft} onClick={() => navigate(-1)} className="self-start">
          Volver
        </Button>
        <Card>
          <p className="text-sm text-slate-600">
            La mascota no tiene un grupo de alimentación asignado o el grupo fue eliminado.
          </p>
        </Card>
      </div>
    );
  }

  const lastFedMs = pet.lastFed?.toMillis?.() ?? null;
  const isLearning = group.learningStatus === LEARNING_STATUS.LEARNING;
  const dispensers = group.dispensers ?? [];
  const hasDispensers = dispensers.length > 0;

  return (
    <div className="flex flex-col gap-4">
      <Button variant="ghost" size="sm" leftIcon={ArrowLeft} onClick={() => navigate(-1)} className="self-start">
        Volver
      </Button>

      {/* Header: foto + nombre + estado */}
      <Card>
        <div className="flex items-center gap-4">
          <Avatar src={petPhotoUrl} name={pet.name} size="lg" />
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold text-slate-900 truncate">{pet.name}</h1>
            <p className="text-sm text-slate-500">
              {pet.species === 'dog' && 'Perro'}
              {pet.species === 'cat' && 'Gato'}
              {pet.species === 'other' && 'Mascota'}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <StatusTrafficLight status={feedingStatus.status} variant="large" />
            <Button
              as={Link}
              to={`/pets/${pet.id}/edit`}
              variant="ghost"
              size="sm"
              aria-label={`Editar ${pet.name}`}
            >
              <Pencil className="h-4 w-4" aria-hidden="true" />
            </Button>
          </div>
        </div>
      </Card>

      {/* Próxima ración */}
      <Card>
        <h2 className="text-sm font-semibold text-slate-900 mb-3">Próxima ración</h2>
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div>
            <p className="text-xs text-slate-500">Última comida</p>
            <p className="text-sm font-medium text-slate-900 mt-0.5">
              {lastFedMs ? formatTimeAgo(lastFedMs) : 'Sin alimentar aún'}
            </p>
          </div>
          <div>
            <p className="text-xs text-slate-500">Falta para la próxima</p>
            <p className="text-sm font-medium text-slate-900 mt-0.5">
              {formatHours(feedingStatus.hoursUntilNext)}
            </p>
          </div>
        </div>
        <FeedingButton pet={pet} variant="full" />
      </Card>

      {isLearning && <LearningBanner pet={pet} group={group} />}

      <EstimationsCard pet={pet} group={group} />

      {hydrationGroup && (
        <HydrationCard
          pet={pet}
          group={hydrationGroup}
          sharedWith={hydrationMates}
          lastWateredByName={lastWateredByName}
        />
      )}

      <HealthMiniSection petId={petId} />

      {/* Inventario + acciones (vive en el grupo) */}
      <Card>
        <div className="flex items-start justify-between mb-3">
          <div>
            <h2 className="text-sm font-semibold text-slate-900">Inventario</h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Grupo: <span className="font-medium text-slate-700">{group.name}</span>
            </p>
          </div>
        </div>

        {groupMates.length > 0 && (
          <div className="mb-3 flex items-start gap-2 rounded-lg bg-slate-50 px-3 py-2 ring-1 ring-slate-100">
            <Users className="h-4 w-4 shrink-0 text-slate-500 mt-0.5" aria-hidden="true" />
            <p className="text-xs text-slate-600">
              Compartido con: <strong>{groupMates.map((p) => p.name).join(', ')}</strong>
            </p>
          </div>
        )}

        {/* Cards por dispenser (0..N) con sus botones individuales */}
        {hasDispensers && (
          <div className="flex flex-col gap-2 mb-3">
            {dispensers.map((d) => {
              const pct = d.capacityGr > 0
                ? Math.round(((d.stockGr ?? 0) / d.capacityGr) * 100)
                : 0;
              return (
                <div key={d.id} className="rounded-lg bg-slate-50 px-3 py-2.5">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{d.name}</p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        {formatGrams(d.stockGr ?? 0)} de {formatGrams(d.capacityGr)} ({pct}%)
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      variant="primary"
                      size="sm"
                      leftIcon={Package}
                      onClick={() => setRefillOpen(d)}
                    >
                      Rellenar
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      leftIcon={CircleSlash}
                      onClick={() => setConfirmEmptyOpen(d)}
                    >
                      Marcar vacío
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Bolsa principal */}
        <div className="rounded-lg bg-slate-50 px-3 py-2 mb-4">
          <p className="text-xs text-slate-500">
            {hasDispensers ? 'Bolsa principal' : 'Bolsa principal (sin dispenser)'}
          </p>
          <p className="text-lg font-bold text-slate-900 mt-0.5">
            {formatGrams(group.mainStockGr)}
          </p>
          <p className="text-xs text-slate-400 mt-0.5">
            {(group.extraBagsCount ?? 0) > 0
              ? `+ ${group.extraBagsCount} en reserva`
              : 'Sin reserva'}
          </p>
        </div>

        {/* Propuesta automática: si la bolsa principal está en 0 y hay reserva. */}
        {group.mainStockGr === 0 && (group.extraBagsCount ?? 0) > 0 && (
          <button
            type="button"
            onClick={() => setOpenBagOpen(true)}
            className="mb-3 flex w-full items-start gap-2.5 rounded-lg bg-amber-50 px-3 py-2.5 text-left ring-1 ring-amber-100 hover:bg-amber-100 transition-colors"
          >
            <AlertCircle className="h-4 w-4 shrink-0 text-amber-600 mt-0.5" aria-hidden="true" />
            <span className="flex-1">
              <span className="block text-sm font-medium text-amber-900">
                La bolsa principal está vacía
              </span>
              <span className="block text-xs text-amber-800 mt-0.5">
                Tenés {group.extraBagsCount} bolsa{group.extraBagsCount === 1 ? '' : 's'} en reserva.
                Tocá acá para abrir una.
              </span>
            </span>
          </button>
        )}

        <div className="flex flex-col gap-2">
          <div className="grid grid-cols-2 gap-2">
            <Button
              variant="secondary"
              size="sm"
              leftIcon={ShoppingBag}
              onClick={() => setOpenBagOpen(true)}
            >
              Abrir bolsa
            </Button>
            <Button
              variant="secondary"
              size="sm"
              leftIcon={Plus}
              onClick={() => setAddBagsOpen(true)}
            >
              Compré bolsas
            </Button>
          </div>
          <Button
            variant="secondary"
            size="sm"
            fullWidth
            leftIcon={Settings2}
            onClick={() => setSyncStockOpen(true)}
          >
            Ajustar stock
          </Button>
        </div>
      </Card>

      {/* Bitácora */}
      <Card>
        <h2 className="text-sm font-semibold text-slate-900 mb-2">Bitácora</h2>
        {logsStatus === 'loading' && logs.length === 0 ? (
          <p className="text-sm text-slate-500 py-4">Cargando actividad...</p>
        ) : logsStatus === 'error' ? (
          <p className="text-sm text-status-red py-4">No se pudo cargar la actividad.</p>
        ) : (
          <LogTimeline
            logs={logs}
            members={members}
            pets={pets}
            groups={groups}
            hydrationGroups={hydrationGroupsList}
          />
        )}
      </Card>

      {/* Modals: cada uno opera sobre un dispenser específico o sobre el grupo. */}
      {refillOpen && (
        <RefillModal
          group={group}
          dispenser={refillOpen}
          isOpen={!!refillOpen}
          onClose={() => setRefillOpen(null)}
        />
      )}
      {confirmEmptyOpen && (
        <ConfirmEmptyModal
          group={group}
          dispenser={confirmEmptyOpen}
          isOpen={!!confirmEmptyOpen}
          onClose={() => setConfirmEmptyOpen(null)}
        />
      )}
      <SyncStockModal
        group={group}
        isOpen={syncStockOpen}
        onClose={() => setSyncStockOpen(false)}
      />
      <OpenBagModal
        group={group}
        isOpen={openBagOpen}
        onClose={() => setOpenBagOpen(false)}
        onBuyBagsClick={() => setAddBagsOpen(true)}
      />
      <AddExtraBagsModal
        group={group}
        isOpen={addBagsOpen}
        onClose={() => setAddBagsOpen(false)}
      />
    </div>
  );
};

export default PetDetailPage;
